// src/features/system/orchestrator/api/scopeMigrateApi.ts
import { bff } from '@/lib/http/bff';

// ── Types ───────────────────────────────────────────────────────────────────

export type ManifestScopeRef = {
  scope_namespace: string;
  scope_key: string;
};

export type ManifestScope = {
  scope_namespace: string;
  scope_key: string;
  parent_ref?: ManifestScopeRef | null;
  created_at: string;
};

export type ManifestOutcome = {
  scope_ref: ManifestScopeRef;
  handler_key: string;
  id_category: number;
  id_type: number;
  value_kind: string;
  value: unknown;
  created_at: string;
};

export type ManifestHotStreamRow = {
  scope_ns: string;
  scope_key: string;
  scope_ref: ManifestScopeRef;
  id_category: number;
  id_type: number;
  value_kind: string;
  value: unknown;
  ts: string;
  created_at: string;
};

export type ManifestGCSFile = {
  source_uri: string;
  rel_path: string;
  /** Content hash ("md5:<hex>" | "crc32c:<hex>"); empty → always-copy. */
  content_hash?: string;
  size_bytes?: number;
  content_type?: string;
};

export type ScopeManifest = {
  version: number;
  source_env: string;
  exported_at: string;
  tenant_id: number;
  root_scope: ManifestScopeRef;
  scopes: ManifestScope[];
  outcomes?: ManifestOutcome[];
  gcs_files?: ManifestGCSFile[];
  hot_stream?: ManifestHotStreamRow[];
  hot_archive_files?: ManifestGCSFile[];
};

/** Conflict resolution strategy for import. */
export type ConflictStrategy = 'skip' | 'upsert' | 'overwrite';

export type ImportResult = {
  dry_run: boolean;
  strategy: ConflictStrategy;
  scopes_created: number;
  scopes_skipped: number;
  scopes_existing: number;
  outcomes_written: number;
  outcomes_updated: number;
  outcomes_skipped: number;
  outcomes_deleted: number;
  gcs_files_copied: number;
  gcs_files_skipped: number;
  gcs_files_failed: number;
  gcs_bytes_transferred: number;
  hot_stream_written: number;
  hot_archive_files_copied: number;
  hot_archive_skipped: number;
  id_map: Record<string, number>;
};

// ── Export params ────────────────────────────────────────────────────────────

export type ExportParams = {
  root_scope_id: number;
  source_env?: string;
  tenant_id?: number;
  include_children?: boolean;
  max_depth?: number;
  include_outcomes?: boolean;
  include_hot_stream?: boolean;
};

// ── Import params ────────────────────────────────────────────────────────────

export type ImportParams = {
  strategy?: ConflictStrategy;
  dry_run?: boolean;
  /** Override target tenant — clones snapshot data into a different tenant. */
  target_tenant_id?: number;
};

// ── API calls ───────────────────────────────────────────────────────────────

export const exportScopeTree = (params: ExportParams): Promise<ScopeManifest> => {
  const qs = new URLSearchParams();
  qs.set('root_scope_id', String(params.root_scope_id));
  if (params.source_env) qs.set('source_env', params.source_env);
  if (params.tenant_id) qs.set('tenant_id', String(params.tenant_id));
  if (params.include_children != null) qs.set('include_children', String(params.include_children));
  if (params.max_depth != null) qs.set('max_depth', String(params.max_depth));
  if (params.include_outcomes != null) qs.set('include_outcomes', String(params.include_outcomes));
  if (params.include_hot_stream != null) qs.set('include_hot_stream', String(params.include_hot_stream));
  return bff.get<ScopeManifest>(`/orchestrator/migrate/scopes/export?${qs}`);
};

/**
 * Batch export — sends all root scope IDs in a single POST request.
 * The orchestrator merges the manifests server-side, avoiding the N-request
 * fan-out that overwhelms browser connection limits.
 */
export const exportScopeTreeBatch = (
  rootScopeIds: number[],
  params: Omit<ExportParams, 'root_scope_id'>,
): Promise<ScopeManifest> => {
  const body: Record<string, unknown> = {
    root_scope_ids: rootScopeIds,
  };
  if (params.source_env) body.source_env = params.source_env;
  if (params.tenant_id) body.tenant_id = params.tenant_id;
  if (params.include_children != null) body.include_children = params.include_children;
  if (params.max_depth != null && params.max_depth > 0) body.max_depth = params.max_depth;
  if (params.include_outcomes != null) body.include_outcomes = params.include_outcomes;
  if (params.include_hot_stream != null) body.include_hot_stream = params.include_hot_stream;
  return bff.post<ScopeManifest>('/orchestrator/migrate/scopes/export/batch', body as any, { timeoutMs: 120_000 });
};

// ── Preview (fast COUNT) ────────────────────────────────────────────────────

/** Server-side preview counts returned before full export. */
export type ExportPreviewCounts = {
  scope_count: number;
  outcome_count: number;
  hot_stream_count: number;
  gcs_file_count: number;
  archive_file_count: number;
};

export type PreviewExportParams = {
  root_scope_ids: number[];
  tenant_id?: number;
  include_children?: boolean;
  max_depth?: number;
  include_outcomes?: boolean;
  include_hot_stream?: boolean;
};

/**
 * Lightweight preview — returns fast DB row counts for the given scope subtrees
 * so the export preview overlay can display accurate numbers.
 */
export const previewExportCounts = (params: PreviewExportParams): Promise<ExportPreviewCounts> => {
  const body: Record<string, unknown> = {
    root_scope_ids: params.root_scope_ids,
  };
  if (params.tenant_id) body.tenant_id = params.tenant_id;
  if (params.include_children != null) body.include_children = params.include_children;
  if (params.max_depth != null && params.max_depth > 0) body.max_depth = params.max_depth;
  if (params.include_outcomes != null) body.include_outcomes = params.include_outcomes;
  if (params.include_hot_stream != null) body.include_hot_stream = params.include_hot_stream;
  return bff.post<ExportPreviewCounts>('/orchestrator/migrate/scopes/export/preview', body as any, { timeoutMs: 30_000 });
};

export const importScopeManifest = (
  manifest: ScopeManifest,
  params?: ImportParams,
): Promise<ImportResult> => {
  const qs = new URLSearchParams();
  if (params?.strategy) qs.set('strategy', params.strategy);
  if (params?.dry_run != null) qs.set('dry_run', String(params.dry_run));
  if (params?.target_tenant_id != null) qs.set('tenant_id', String(params.target_tenant_id));
  const suffix = qs.toString() ? `?${qs}` : '';
  return bff.post<ImportResult>(
    `/orchestrator/migrate/scopes/import${suffix}`,
    manifest as any,
    { timeoutMs: IMPORT_TIMEOUT_MS },
  );
};

// ── Pending import queue (gate/approval) ─────────────────────────────────

/** Metadata for a queued manifest awaiting approval. */
export type PendingImportItem = {
  id: string;
  source_env: string | null;
  source_device_id: string | null;
  queued_at: string;
  scope_count: number;
  outcome_count: number;
  hot_stream_count: number;
  namespace_summary: { ns: string; count: number }[];
  root_scope: { scope_namespace: string; scope_key: string } | null;
  tenant_id: number | null;
  expires_at_ms: number;
};

/** Full pending import entry including manifest body. */
export type PendingImportDetail = PendingImportItem & {
  manifest: ScopeManifest;
};

/** Result returned by the /queue endpoint. */
export type QueueResult = {
  status: 'queued';
  id: string;
  queued_at: string;
  message: string;
};

export const fetchPendingImports = (): Promise<PendingImportItem[]> =>
  bff.get<PendingImportItem[]>('/orchestrator/migrate/scopes/pending');

export const fetchPendingImportDetail = (id: string): Promise<PendingImportDetail> =>
  bff.get<PendingImportDetail>(`/orchestrator/migrate/scopes/pending/${id}`);

// Importing thousands of outcomes is slow (individual INSERTs + trigger
// checks).  The default 12 s client timeout is far too short; 3 min covers
// large manifests on cold Cloud Run instances.
const IMPORT_TIMEOUT_MS = 180_000;

export const approvePendingImport = (
  id: string,
  params?: ImportParams,
): Promise<ImportResult> => {
  const qs = new URLSearchParams();
  if (params?.strategy) qs.set('strategy', params.strategy);
  if (params?.dry_run != null) qs.set('dry_run', String(params.dry_run));
  if (params?.target_tenant_id != null) qs.set('tenant_id', String(params.target_tenant_id));
  const suffix = qs.toString() ? `?${qs}` : '';
  return bff.post<ImportResult>(
    `/orchestrator/migrate/scopes/pending/${id}/approve${suffix}`,
    {} as any,
    { timeoutMs: IMPORT_TIMEOUT_MS },
  );
};

export const rejectPendingImport = (id: string): Promise<{ status: string; id: string }> =>
  bff.del<{ status: string; id: string }>(`/orchestrator/migrate/scopes/pending/${id}`);
